/**
 * Workflow field -> workflow links: `linkedWorkflowRules[]` on a field in
 * `fieldsMetadata`, a sibling of `visibilityRules` using the identical condition
 * vocabulary. Each entry is
 *   { linkedWorkflowId, conditions: [{ sourceUniqueKey, expectedValue }] }
 * with conditions ANDed inside an entry and entries ORed against each other.
 *
 * The maker UI drives the common shape: the operator picks one of the field's own
 * options and a workflow, which is one rule per (option, workflow) pair whose
 * single condition self-references the field. `expectedValue` is the visible
 * option label (`inputValue`), not `inputKey` -- task_data_json stores the label,
 * so that's what the reports match on.
 *
 * /workflow/update rewrites linked_workflow_json from whatever is sent, so the
 * rules loaded from get-workflow-metadata have to be echoed back on save;
 * omitting them (or sending []) erases the links. Rules the per-option UI can't
 * represent (extra ANDed conditions, or a condition pointing at another field)
 * are therefore left untouched by every editor below and surfaced read-only.
 */

import { flattenWorkflowFields } from "./workflowCompareValidation";

/** Backend refuses a link on any other field type. */
export const LINKABLE_FIELD_TYPES = ["radio", "checkbox", "select"];

export function isLinkableFieldType(fieldType) {
  return LINKABLE_FIELD_TYPES.includes(fieldType);
}

export function getLinkedWorkflowRules(field) {
  return Array.isArray(field?.linkedWorkflowRules)
    ? field.linkedWorkflowRules
    : [];
}

export function isLinkedWorkflowMarkerField(field) {
  return (
    field?.is_linked_workflow_field === true ||
    field?.isLinkedWorkflowField === true
  );
}

const LINKED_WORKFLOW_MARKER_NAME = "Linked Workflow";
const LINKED_WORKFLOW_MARKER_TOOLTIP = "Enter the name for the field.";

/** A rule the per-option dropdown owns: one condition, pointing at this field's own option. */
function isSimpleOptionRule(rule, uniqueKey, optionValue) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (conditions.length !== 1) return false;
  return (
    conditions[0]?.sourceUniqueKey === uniqueKey &&
    conditions[0]?.expectedValue === optionValue
  );
}

function isSelfReferencingSimpleRule(rule, uniqueKey) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  return conditions.length === 1 && conditions[0]?.sourceUniqueKey === uniqueKey;
}

export function getLinkedWorkflowIdForOption(field, optionValue) {
  const rule = getLinkedWorkflowRules(field).find((r) =>
    isSimpleOptionRule(r, field?.uniqueKey, optionValue)
  );
  return rule?.linkedWorkflowId ?? null;
}

/**
 * Rules the per-option UI can't show as a single dropdown -- multi-condition
 * (ANDed) rules and rules driven by another field. Listed read-only so the maker
 * can see they exist, since save echoes them back untouched.
 */
export function getAdvancedLinkedWorkflowRules(field) {
  return getLinkedWorkflowRules(field).filter(
    (rule) => !isSelfReferencingSimpleRule(rule, field?.uniqueKey)
  );
}

/**
 * Set (or clear, with a null/empty workflowId) the workflow linked to one option.
 * Only the matching single-condition rule is replaced; every other rule survives.
 */
export function setLinkedWorkflowForOption(field, optionValue, workflowId) {
  const rest = getLinkedWorkflowRules(field).filter(
    (rule) => !isSimpleOptionRule(rule, field?.uniqueKey, optionValue)
  );

  if (workflowId === null || workflowId === undefined || workflowId === "") {
    return rest;
  }

  return [
    ...rest,
    {
      linkedWorkflowId: Number(workflowId),
      conditions: [
        { sourceUniqueKey: field.uniqueKey, expectedValue: optionValue },
      ],
    },
  ];
}

function mapFields(items, mapper) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item || typeof item !== "object") return item;
    const next = mapper({ ...item });
    if (next.radioInputValues?.length) {
      next.radioInputValues = mapFields(next.radioInputValues, mapper);
    }
    return next;
  });
}

/** Follow an option label rename so the rule keeps matching the option it was set on. */
export function renameLinkedWorkflowExpectedValue(
  items,
  sourceUniqueKey,
  oldExpectedValue,
  newExpectedValue
) {
  if (
    oldExpectedValue === undefined ||
    oldExpectedValue === null ||
    oldExpectedValue === "" ||
    oldExpectedValue === newExpectedValue
  ) {
    return items;
  }

  return mapFields(items, (field) => {
    const rules = getLinkedWorkflowRules(field);
    if (rules.length === 0) return field;
    return {
      ...field,
      linkedWorkflowRules: rules.map((rule) => ({
        ...rule,
        conditions: (rule.conditions || []).map((condition) =>
          condition?.sourceUniqueKey === sourceUniqueKey &&
          condition?.expectedValue === oldExpectedValue
            ? { ...condition, expectedValue: newExpectedValue }
            : condition
        ),
      })),
    };
  });
}

/**
 * Drop every rule that depends on a deleted option. Unlike visibility rules the
 * condition isn't just removed from the rule: the conditions are ANDed, so
 * dropping one would widen the rule into linking a workflow on cases the operator
 * never picked.
 */
export function removeLinkedWorkflowRulesForOption(
  items,
  sourceUniqueKey,
  expectedValue
) {
  return mapFields(items, (field) => {
    const rules = getLinkedWorkflowRules(field);
    if (rules.length === 0) return field;

    const kept = rules.filter(
      (rule) =>
        !(rule.conditions || []).some(
          (condition) =>
            condition?.sourceUniqueKey === sourceUniqueKey &&
            condition?.expectedValue === expectedValue
        )
    );

    if (kept.length === rules.length) return field;
    const next = { ...field };
    if (kept.length) {
      next.linkedWorkflowRules = kept;
    } else {
      delete next.linkedWorkflowRules;
    }
    return next;
  });
}

/**
 * Copy the rules from `fieldsMetadata` onto the `dragItems` tree the canvas is
 * seeded from, matched by uniqueKey. get-workflow-metadata documents
 * linkedWorkflowRules on the fields it returns; dragItems is the maker's own
 * blob, so this keeps the links intact whether or not they were mirrored into it.
 * Accepts either shape on both sides (the API lists dependents flat, the canvas
 * nests them under radioInputValues).
 */
export function hydrateLinkedWorkflowRules(dragItems, fieldsMetadata) {
  if (!Array.isArray(dragItems) || dragItems.length === 0) return dragItems;

  const rulesByKey = new Map();
  flattenWorkflowFields(fieldsMetadata).forEach((field) => {
    const rules = getLinkedWorkflowRules(field);
    if (field?.uniqueKey && rules.length) {
      rulesByKey.set(field.uniqueKey, rules);
    }
  });
  if (rulesByKey.size === 0) return dragItems;

  return mapFields(dragItems, (field) => {
    const rules = rulesByKey.get(field.uniqueKey);
    return rules ? { ...field, linkedWorkflowRules: rules } : field;
  });
}

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

function normalizeConditions(conditions) {
  return (Array.isArray(conditions) ? conditions : [])
    .filter(
      (condition) =>
        !isBlank(condition?.sourceUniqueKey) &&
        !isBlank(condition?.expectedValue)
    )
    .map((condition) => ({
      sourceUniqueKey: condition.sourceUniqueKey,
      expectedValue: condition.expectedValue,
    }));
}

function ruleSignature(rule) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  const key = conditions
    .map((condition) => `${condition?.sourceUniqueKey}:${condition?.expectedValue}`)
    .join("|");
  return `${Number(rule?.linkedWorkflowId)}|${key}`;
}

function mergeLinkedWorkflowRules(existing = [], incoming = []) {
  const merged = new Map();
  existing.forEach((rule) => {
    const normalized = {
      linkedWorkflowId: Number(rule.linkedWorkflowId),
      conditions: normalizeConditions(rule.conditions),
    };
    if (normalized.conditions.length === 0 || isBlank(normalized.linkedWorkflowId)) return;
    merged.set(ruleSignature(normalized), normalized);
  });
  incoming.forEach((rule) => {
    const normalized = {
      linkedWorkflowId: Number(rule.linkedWorkflowId),
      conditions: normalizeConditions(rule.conditions),
    };
    if (normalized.conditions.length === 0 || isBlank(normalized.linkedWorkflowId)) return;
    merged.set(ruleSignature(normalized), normalized);
  });
  return Array.from(merged.values());
}

export function splitLinkedWorkflowMarkerFields(fieldsMetadata = []) {
  const markerFields = [];
  const normalFields = [];
  (fieldsMetadata || []).forEach((field) => {
    if (isLinkedWorkflowMarkerField(field)) {
      markerFields.push(field);
    } else {
      normalFields.push(field);
    }
  });
  return { markerFields, normalFields };
}

export function stripLinkedWorkflowMarkerFields(items) {
  if (!Array.isArray(items)) return items;
  return items.reduce((acc, item) => {
    if (!item || typeof item !== "object") return acc;
    if (isLinkedWorkflowMarkerField(item)) return acc;
    const next = { ...item };
    if (next.radioInputValues?.length) {
      next.radioInputValues = stripLinkedWorkflowMarkerFields(
        next.radioInputValues
      );
    }
    acc.push(next);
    return acc;
  }, []);
}

export function applyLinkedWorkflowMarkers(items, markerFields) {
  if (!Array.isArray(items) || !Array.isArray(markerFields)) return items;
  if (markerFields.length === 0) return items;

  const rulesBySource = new Map();
  markerFields.forEach((marker) => {
    const linkedWorkflowId =
      marker?.linkedWorkflowId ?? marker?.linked_workflow_id;
    if (isBlank(linkedWorkflowId)) return;
    const visibilityRules = Array.isArray(marker.visibilityRules)
      ? marker.visibilityRules
      : [];
    visibilityRules.forEach((rule) => {
      const conditions = normalizeConditions(rule?.conditions);
      if (conditions.length === 0) return;
      const sourceKey = conditions[0]?.sourceUniqueKey;
      if (isBlank(sourceKey)) return;
      const ruleEntry = {
        linkedWorkflowId: Number(linkedWorkflowId),
        conditions,
      };
      const list = rulesBySource.get(sourceKey) || [];
      list.push(ruleEntry);
      rulesBySource.set(sourceKey, list);
    });
  });

  if (rulesBySource.size === 0) return items;

  return mapFields(items, (field) => {
    if (!field?.uniqueKey) return field;
    const incoming = rulesBySource.get(field.uniqueKey);
    if (!incoming || incoming.length === 0) return field;
    const merged = mergeLinkedWorkflowRules(
      getLinkedWorkflowRules(field),
      incoming
    );
    return merged.length ? { ...field, linkedWorkflowRules: merged } : field;
  });
}

export function stripLinkedWorkflowRules(items) {
  return mapFields(items, (field) => {
    if (!field || typeof field !== "object") return field;
    if (!field.linkedWorkflowRules) return field;
    const next = { ...field };
    delete next.linkedWorkflowRules;
    return next;
  });
}

export function buildLinkedWorkflowMarkerFields(items, options = {}) {
  const {
    fieldName = LINKED_WORKFLOW_MARKER_NAME,
    tooltip = LINKED_WORKFLOW_MARKER_TOOLTIP,
  } = options;
  const fields = flattenWorkflowFields(items);
  const pageByKey = new Map(
    fields
      .filter((field) => field?.uniqueKey)
      .map((field) => [field.uniqueKey, field.page])
  );
  let maxQueNo = Math.max(
    0,
    ...fields.map((field) => Number(field?.queNo) || 0)
  );
  const baseKey = Date.now();
  let counter = 0;
  const markerFields = [];

  fields.forEach((field) => {
    const rules = getLinkedWorkflowRules(field);
    if (rules.length === 0) return;
    rules.forEach((rule) => {
      if (isBlank(rule?.linkedWorkflowId)) return;
      const conditions = normalizeConditions(rule.conditions);
      if (conditions.length === 0) return;
      const sourceKey = conditions[0]?.sourceUniqueKey;
      const page = pageByKey.get(sourceKey) ?? field?.page ?? 1;
      maxQueNo += 1;
      markerFields.push({
        fieldName,
        value: "",
        fieldType: "text",
        isMandatory: false,
        isReport: true,
        cloneName: fieldName,
        reportName: fieldName,
        excelReportName: fieldName,
        validationValues: [],
        tooltip,
        uniqueKey: `${baseKey}${counter++}`,
        page,
        queNo: maxQueNo,
        radioId: null,
        visibilityRules: [{ conditions }],
        isLinkedWorkflowField: true,
        linkedWorkflowId: Number(rule.linkedWorkflowId),
      });
    });
  });

  return {
    cleanedItems: stripLinkedWorkflowRules(items),
    markerFields,
  };
}

/**
 * Strip anything the backend would reject, so a partially-configured rule can't
 * fail the whole save. With the per-option UI this is a safety net -- picking a
 * workflow is what creates a rule -- but a rule loaded from an older payload can
 * still be incomplete.
 */
export function mapFieldsSanitizeLinkedWorkflowRules(items) {
  return mapFields(items, (field) => {
    const rules = getLinkedWorkflowRules(field);
    if (rules.length === 0) return field;

    const next = { ...field };

    if (!isLinkableFieldType(field.fieldType)) {
      delete next.linkedWorkflowRules;
      return next;
    }

    const kept = rules
      .filter((rule) => !isBlank(rule?.linkedWorkflowId))
      .map((rule) => ({
        linkedWorkflowId: Number(rule.linkedWorkflowId),
        conditions: (rule.conditions || [])
          .filter(
            (condition) =>
              !isBlank(condition?.sourceUniqueKey) &&
              !isBlank(condition?.expectedValue)
          )
          .map((condition) => ({
            sourceUniqueKey: condition.sourceUniqueKey,
            expectedValue: condition.expectedValue,
          })),
      }))
      .filter((rule) => rule.conditions.length > 0);

    if (kept.length) {
      next.linkedWorkflowRules = kept;
    } else {
      delete next.linkedWorkflowRules;
    }
    return next;
  });
}

/**
 * The same checks /workflow/update rejects with code 601, run client-side so the
 * maker reports which field is at fault instead of a bare toast from the server.
 * Returns an error message, or null when everything is valid.
 *
 * `allowedWorkflowIds` is the project's workflow picker list (get-workflow-list
 * already drops static and safety sub-workflows); pass null to skip the
 * "cannot be linked" check, e.g. while the list is still loading.
 * `currentWorkflowId` is the workflow being saved -- a workflow cannot link
 * itself, and that's the one exclusion the UI has to make on its own.
 */
export function validateLinkedWorkflowRules(
  items,
  { allowedWorkflowIds = null, currentWorkflowId = null, messages = {} } = {}
) {
  const fields = flattenWorkflowFields(items);
  const knownKeys = new Set(
    fields.map((field) => field.uniqueKey).filter(Boolean)
  );
  const allowed =
    allowedWorkflowIds === null
      ? null
      : new Set((allowedWorkflowIds || []).map((id) => Number(id)));
  const selfId = isBlank(currentWorkflowId) ? null : Number(currentWorkflowId);

  const text = {
    notLinkable: (name, type) =>
      `A linked workflow can only be configured on a radio, checkbox or dropdown field - "${name}" is not one (${type})`,
    missingId: (name) =>
      `Linked workflow rule on "${name}" is missing its linked workflow`,
    noConditions: (name) =>
      `Linked workflow rule on "${name}" needs at least one option selected`,
    blankCondition: (name) =>
      `Linked workflow rule on "${name}" needs a source field and an expected value on every condition`,
    unknownField: (name, key) =>
      `Linked workflow rule on "${name}" points at unknown field "${key}"`,
    selfLink: (name) =>
      `Linked workflow rule on "${name}" points at the workflow being saved - a workflow cannot link itself`,
    inactiveWorkflow: (id) =>
      `Linked workflow ${id} cannot be linked - it must be an active, non-static workflow of this project and not a safety sub-workflow`,
    ...messages,
  };

  for (const field of fields) {
    const rules = getLinkedWorkflowRules(field);
    if (rules.length === 0) continue;

    const name = field.fieldName || field.uniqueKey;

    if (!isLinkableFieldType(field.fieldType)) {
      return text.notLinkable(name, field.fieldType);
    }

    for (const rule of rules) {
      if (isBlank(rule?.linkedWorkflowId)) return text.missingId(name);

      const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
      if (conditions.length === 0) return text.noConditions(name);

      for (const condition of conditions) {
        if (
          isBlank(condition?.sourceUniqueKey) ||
          isBlank(condition?.expectedValue)
        ) {
          return text.blankCondition(name);
        }
        if (!knownKeys.has(condition.sourceUniqueKey)) {
          return text.unknownField(name, condition.sourceUniqueKey);
        }
      }

      // Checked before the picker list so a self-link reports as such rather
      // than as a generic "cannot be linked" -- the open workflow is excluded
      // from that list precisely because the backend refuses it.
      if (selfId !== null && Number(rule.linkedWorkflowId) === selfId) {
        return text.selfLink(name);
      }

      if (allowed && !allowed.has(Number(rule.linkedWorkflowId))) {
        return text.inactiveWorkflow(rule.linkedWorkflowId);
      }
    }
  }

  return null;
}

/** "Tampered -> Meter Tamper Report" style summary, for the read-only advanced list. */
export function formatLinkedWorkflowRuleSummary(
  items,
  rule,
  workflowNameById = {}
) {
  const fields = flattenWorkflowFields(items);
  const nameFor = (uniqueKey) =>
    fields.find((field) => field.uniqueKey === uniqueKey)?.fieldName ??
    uniqueKey;

  const conditions = (rule?.conditions || [])
    .map((condition) => `${nameFor(condition.sourceUniqueKey)} = ${condition.expectedValue}`)
    .join(" AND ");

  const workflowName =
    workflowNameById[rule?.linkedWorkflowId] ??
    `Workflow ${rule?.linkedWorkflowId}`;

  return conditions ? `${conditions} → ${workflowName}` : workflowName;
}
